/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
	FormControl,
	Grid,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
	TextField,
	Box,
	InputAdornment,
	CircularProgress,
	ListItemText
} from '@mui/material';
import { IoSearch } from 'react-icons/io5';
import ProfilePic from '../../ProfilePic';
import { useSelector } from 'react-redux';
import { getEmployeePermissions } from '../../../../containers/actions/resource_configuration/employee_permissions';
import { EmployeeWithPermissions } from '../../../../containers/actions/resource_configuration/types';
import { useSearchParams } from 'react-router-dom';

interface CustomMembersFilterProps {
	labelText?: string;
	value?: string;
	onChange?: (value: string) => void;
}

const CustomMembersFilter: React.FC<CustomMembersFilterProps> = ({
	labelText = 'Members',
	value,
	onChange
}) =>
{
	const [ searchParams, setSearchParams ] = useSearchParams();
	const queryParamValue = searchParams.get('memberFilter');
	const [ localValue, setLocalValue ] = useState<string>(queryParamValue || 'all');
	const [ searchText, setSearchText ] = useState<string>('');
	const [ filteredEmployees, setFilteredEmployees ] = useState<EmployeeWithPermissions[]>([]);
	const [ menuOpen, setMenuOpen ] = useState<boolean>(false);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ employeeData, setEmployeeData ] = useState<EmployeeWithPermissions[]>([]);
	const [ error, setError ] = useState<string | null>(null);

	const isControlled = value !== undefined && onChange !== undefined;
	// Determine which value to use based on whether component is controlled
	const selectedValue = isControlled ? value : localValue;

	// Get session data from Redux store to access companyId
	const session = useSelector((state: any) => state?.mysession?.session);
	const companyId = session?.companyId || '';

	// Fetch employee permissions data
	const fetchEmployeePermissions = async () =>
	{
		if (!companyId) return;

		setLoading(true);
		setError(null);

		try
		{
			const response = await getEmployeePermissions(companyId);

			if (response && response.employeePermissions)
			{
				setEmployeeData(response.employeePermissions);
			}
		}
		catch (err)
		{
			console.error('Error fetching employee permissions:', err);
			setError('Failed to load employee data');
		}
		finally
		{
			setLoading(false);
		}
	};

	// Initialize from URL query param if not controlled
	useEffect(() =>
	{
		if (!isControlled)
		{
			setLocalValue(queryParamValue || 'all');
		}
	}, [ queryParamValue ]);

	// Fetch employee data when component mounts or companyId changes
	useEffect(() =>
	{
		if (companyId)
		{
			fetchEmployeePermissions();
		}
	}, [ companyId ]);

	// Filter employees based on search text
	useEffect(() =>
	{
		if (searchText.trim() === '')
		{
			setFilteredEmployees(employeeData);
		}
		else
		{
			setFilteredEmployees(
				employeeData.filter((employee) =>
				{
					const fullName = `${employee.name || ''}`.trim().toLowerCase();

					return fullName.includes(searchText.trim().toLowerCase());
				})
			);
		}
	}, [ searchText, employeeData ]);

	const handleSelectChange = (event: any) =>
	{
		const newValue = typeof event === 'string' ? event : event.target.value;

		if (isControlled)
		{
			onChange(newValue);
		}
		else
		{
			setLocalValue(newValue);

			// Update query params
			searchParams.set('memberFilter', newValue);
			setSearchParams(searchParams);
		}

		setMenuOpen(false);
		setSearchText('');
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
	{
		setSearchText(event.target.value);
	};

	const renderSelectedValue = () =>
	{
		if (selectedValue === 'all') return 'All';

		const selectedEmployee = employeeData.find((employee) =>
			(employee?.employeeId) === selectedValue
		);

		if (selectedEmployee)
		{
			const displayName = `${selectedEmployee.name || ''}`.trim();

			return (
				<Grid container direction='row' display={'flex'} flexWrap='nowrap' alignItems='center'>
					<Grid item display={'flex'} justifyContent={'center'}>
						<ProfilePic
							imagePath={`${selectedEmployee.employeeId}_profile.jpeg`}
							defaultImg='/images/icons/defaultUser.svg'
							alt='profile'
							className='brdrRadius50 imgAvatarXXS'
							style={{
								width  : '20px',
								height : '20px'
							}}
						/>
					</Grid>
					<Grid item minWidth={0} px={1}>
						<Typography overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap' maxWidth='200px' fontSize='var(--pixel14)'>
							{displayName}
						</Typography>
					</Grid>
				</Grid>
			);
		}

		return 'All';
	};

	return (
		<Grid item>
			<Typography color='var(--darkgray)' fontSize='var(--pixel10)'>
				{labelText}
			</Typography>
			<FormControl fullWidth>
				<Select
					value={selectedValue}
					onChange={(event: SelectChangeEvent<string>) => handleSelectChange(event)}
					renderValue={renderSelectedValue}
					open={menuOpen}
					onOpen={() => setMenuOpen(true)}
					onClose={() => setMenuOpen(false)}
					sx={{
						backgroundColor                      : 'var(--lightgray)',
						border                               : 'none',
						borderRadius                         : 'var(--pixel4)',
						boxShadow                            : 'none',
						minWidth                             : '220px',
						maxWidth                             : '200px',
						width                                : '100%',
						'& .MuiOutlinedInput-notchedOutline' : {
							border : 'none'
						},
						'&:hover' : {
							backgroundColor : '#3FBBE11A'
						},
						'& .MuiSelect-select' : {
							padding : '4px 10px'
						}
					}}
					MenuProps={{
						disableAutoFocusItem : true,
						PaperProps           : {
							style       : { maxHeight: 300, overflow: 'auto', maxWidth: '220px', width: '100%' },
							onMouseDown : (e) => e.stopPropagation()
						},
						MenuListProps : {
							sx : {
								paddingTop : '0px',
								marginTop  : '0px',
								'& ul'     : {
									paddingTop : '0px !important'
								}
							}
						}
					}}
				>
					<Box
						px='var(--pixel16)'
						pb='8px'
						pt={2}
						sx={{
							position        : 'sticky',
							top             : 0,
							backgroundColor : 'white',
							zIndex          : 1
						}}
						onMouseDown={(e) => e.stopPropagation()}
					>
						<TextField
							placeholder='Search'
							variant='outlined'
							size='small'
							fullWidth
							value={searchText}
							onChange={handleSearchChange}
							InputProps={{
								startAdornment : (
									<InputAdornment position='start'>
										<IoSearch />
									</InputAdornment>
								)
							}}
							onClick={(e) => e.stopPropagation()}
							onMouseDown={(e) => e.stopPropagation()}
							sx={{
								'& .MuiOutlinedInput-root' : {
									paddingLeft : '4px' // Reduce the left padding of input
								}
							}}
						/>
					</Box>

					<MenuItem
						onClick={() => handleSelectChange('all')}
						value='all'
					>
						<ListItemText primary='All' />
					</MenuItem>

					{filteredEmployees.map((employee) =>
					{
						const employeeId = employee?.employeeId;
						const displayName = `${employee.name || ''}`.trim();

						return (
							<MenuItem
								key={employeeId}
								value={employeeId}
								onClick={() => handleSelectChange(employeeId)}
							>
								<Grid container direction='row' py={0.5} display={'flex'} wrap='nowrap' alignItems='center'>
									<Grid item display={'flex'} justifyContent={'center'}>
										<ProfilePic
											imagePath={`${employee.employeeId}_profile.jpeg`}
											defaultImg='/images/icons/defaultUser.svg'
											alt='profile'
											className='brdrRadius50 imgAvatarXXSM'
											// from='selfProfile'
											style={{
												width  : '20px',
												height : '20px'
											}}
										/>
									</Grid>
									<Grid item minWidth={0} px={1}>
										<Typography
											sx={{
												overflow     : 'hidden',
												textOverflow : 'ellipsis',
												whiteSpace   : 'nowrap',
												display      : 'block',
												maxWidth     : '120px'
											}}
											title={displayName}
										>
											{displayName}
										</Typography>
									</Grid>
								</Grid>
							</MenuItem>
						);
					})}

					{filteredEmployees.length === 0 && !loading && (
						<MenuItem disabled>
							<Typography color='textSecondary'>No results found</Typography>
						</MenuItem>
					)}

					{loading && (
						<Box
							sx={{
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								py             : 'var(--pixel16)'
							}}
						>
							<CircularProgress size={20} />
						</Box>
					)}

					{error && (
						<MenuItem disabled>
							<Typography color='error'>{error}</Typography>
						</MenuItem>
					)}
				</Select>
			</FormControl>
		</Grid>
	);
};

export default CustomMembersFilter;
